import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useWhatsAppWhitelist,
  useSaveWhitelist,
} from "@/hooks/useWhatsAppWhitelist";

function parsePhones(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[,\n]+/)
        .map((s) => s.replace(/\D/g, ""))
        .filter((s) => s.length >= 7),
    ),
  ];
}

export function WhatsAppWhitelist(): React.JSX.Element {
  const { data, isLoading } = useWhatsAppWhitelist();
  const { mutate: save, isPending } = useSaveWhitelist();

  const [chips, setChips] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [dirty, setDirty] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (data && !initialized.current) {
      setChips(data.phones);
      initialized.current = true;
    }
  }, [data]);

  function handleAdd(): void {
    const parsed = parsePhones(inputValue);
    if (!parsed.length) return;
    const existing = new Set(chips);
    const next = [...chips, ...parsed.filter((p) => !existing.has(p))];
    setChips(next);
    setInputValue("");
    setDirty(true);
  }

  function handleRemove(phone: string): void {
    setChips((prev) => prev.filter((p) => p !== phone));
    setDirty(true);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  }

  function handleSave(): void {
    save(chips, { onSuccess: () => setDirty(false) });
  }

  return (
    <div className="w-full max-w-sm rounded-xl border border-ink-100 bg-white p-8 shadow-sm">
      <div className="mb-5">
        <p className="text-base font-semibold text-ink-900">
          Números permitidos
        </p>
        <p className="mt-1 text-sm text-ink-500">
          Si hay números en esta lista, el bot solo responderá a ellos. Si está
          vacía, se aplican las reglas de bloqueo.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <textarea
          className="w-full resize-none rounded-lg border border-ink-200 bg-ink-050 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-400 focus:outline-none"
          rows={3}
          placeholder="Pega números separados por comas o saltos de línea"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
        >
          Agregar
        </Button>
      </div>

      {isLoading ? (
        <p className="mt-4 text-xs text-ink-400">Cargando…</p>
      ) : chips.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((phone) => (
            <span
              key={phone}
              className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-3 py-1 font-mono text-xs text-ink-700"
            >
              {phone}
              <button
                type="button"
                onClick={() => handleRemove(phone)}
                className="ml-0.5 rounded-full text-ink-400 hover:text-ink-700"
                aria-label={`Eliminar ${phone}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-ink-400">
          Sin números permitidos — el bot responde a todos (excepto bloqueados).
        </p>
      )}

      <div className="mt-6 flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={!dirty || isPending}>
          {isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}
